/**
 * DubDialog.jsx — the same piece, another language.
 *
 * Deliberately plain: pick a language, decide whether it should match the
 * original's running time, and Rammy makes a new editable project you can read
 * before spending a render on it.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import * as L from "lucide-react";

export function DubDialog({ projectId, onCreated, onClose }) {
  const [languages, setLanguages] = useState([]);
  const [picked, setPicked] = useState(null);
  const [matchTiming, setMatchTiming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/studio/dub/languages")
      .then(r => setLanguages(r.data?.languages || []))
      .catch(() => {});
  }, []);

  const go = async () => {
    if (!picked) { toast.error("Pick a language first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/studio/projects/${projectId}/dub`, {
        language: picked, match_timing: matchTiming,
      }, { timeout: 240000 });
      toast.success(`${data.language_label} version ready — read it, then render`);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't translate that");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="studio-dub-dialog"
    >
      <div
        className="w-full max-w-lg rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 shadow-[0_24px_60px_rgba(17,19,24,0.28)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl">Another language</h2>
            <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
              Rammy translates the script for the ear and keeps your voices, pauses and music.
              You'll get a new project to read before you render it.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-[var(--rammy-text-muted)] hover:bg-[var(--rammy-surface-alt)]"
            data-testid="studio-dub-close"
            aria-label="Close"
          >
            <L.X size={16}/>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-5">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => setPicked(lang.code)}
              data-active={picked === lang.code ? "true" : "false"}
              data-testid={`studio-dub-lang-${lang.code}`}
              className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                picked === lang.code
                  ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/12"
                  : "border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)]"
              }`}
            >
              <span className="block text-[13px] font-medium">{lang.native}</span>
              <span className="block text-[10.5px] text-[var(--rammy-text-muted)]">{lang.label}</span>
            </button>
          ))}
        </div>

        <label className="flex items-start gap-2.5 mt-5 cursor-pointer">
          <input
            type="checkbox"
            checked={matchTiming}
            onChange={(e) => setMatchTiming(e.target.checked)}
            className="mt-0.5 accent-[var(--rammy-cyan-dark)]"
            data-testid="studio-dub-match-timing"
          />
          <span className="text-[12.5px] text-[var(--rammy-text-sec)] leading-relaxed">
            <span className="font-medium text-[var(--rammy-text)]">Match the original's length</span> — useful
            if the audio has to sit against the same video. It can make some languages sound a little rushed.
          </span>
        </label>

        <div className="flex items-center justify-end gap-3 mt-6">
          {!picked && (
            <span className="text-[11.5px] text-[var(--rammy-text-muted)] mr-auto" data-testid="studio-dub-hint">
              Pick a language to continue.
            </span>
          )}
          <Button variant="ghost" onClick={onClose} className="btn-pill h-9 text-xs" data-testid="studio-dub-cancel">
            Cancel
          </Button>
          {/* Deliberately NOT disabled on `!picked`: a dead button with a
              small grey hint is easy to miss. Pressing it says why. */}
          <Button onClick={go} disabled={busy} className="btn-pill h-9 text-xs" data-testid="studio-dub-go">
            {busy ? <L.Loader2 size={13} className="animate-spin"/> : <L.Languages size={13}/>}
            {busy ? "Translating…" : "Make the version"}
          </Button>
        </div>
      </div>
    </div>
  );
}
